import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    LOCALE_ID,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RoutesRecognized } from '@angular/router';
import { select } from '@angular-redux/store';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';

import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    CustomerAuthFacade,
    DeviceNotificationService,
    LoginFacade,
    NotificationFacade,
    OpenTripPlannerFacade,
    SessionService
} from '@ridango/components';
import {
    AppStartupService,
    CoreService,
    CoreFacade,
    Language,
    BaseComponent,
    MtbService,
    CacheService,
    OnboardingService,
    NetworkService,
    AuthService,
    MobileHelperService,
    Platform,
    ModalFocusInterceptorService
} from '@ridango/core';

import { Observable, of, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { MatIconRegistry } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ModalController } from '@ionic/angular';
import { MapApiService, OnboardingType, WidgetsService } from '@ridango/widgets';
import Env from '@ridango/core/env';
import { LocaleService } from './services/locale.service';
import { PullToRefreshService } from '@ridango/core/lib/services/pull-to-refresh.service';
import { AnalyticsService, VendorType } from '@ridango/core/lib/services/analytics.service';
import { PrintService } from './services/print.service';
import { Keyboard } from '@capacitor/keyboard';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AccountTicketsService } from '@ridango/components/lib/account-tickets-ngrx/services/account-tickets.service';
import { StatusBarService } from '@ridango/core/lib/services/status-bar.service';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { IonContent } from '@ionic/angular';
import { HelperService } from './shared/services/helper.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { KeyboardService } from '@ridango/core/lib/services/keyboard.service';
import { TranslationsService } from './services/translations.service';
import { MessagesService } from './services/messages.service';
import { AuthInterceptor, TicketsService } from '@ridango/core/lib/services';
import { NgRedux } from '@angular-redux/store';
import { AppState } from './app.store';

enum AppFooterType {
    navigation = 'navigation',
    back = 'back'
}

const RESET_TICKETS_TIME_CACHE = 'RESET_TICKETS_TIME';
export const TICKETS_CACHE_CLEARED = 'TICKETS_CACHE_CLEARED';

@Component({
    selector: 'gl-root',
    templateUrl: './app.component.html'
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(IonContent, { static: false }) ionContent!: IonContent;
    @ViewChild('ionContentRef', { read: ElementRef }) ionContentRef!: ElementRef<HTMLIonContentElement>;
    regionLanguages$: Observable<Language[]> = this.coreFacade.regionLanguages$;
    regionResetTicketsTime$: Observable<number> = this.coreFacade.regionResetTicketsTime$;
    resetTicketsStateTimer: ReturnType<typeof setTimeout>;
    private shouldReloadTicketsOnOnline = false;
    splashScreenRemoved = false;
    isFooterHidden = false;
    termsModalVisible$ = this.loginFacade.termsModalVisible$;
    termsUrl: string;
    terms = null;

    constructor(
        private coreFacade: CoreFacade,
        @Inject(DOCUMENT) private document: Document,
        @Inject('ENV') public env: Env,
        private router: Router,
        @Inject(LOCALE_ID) public locale: string,
        public translate: TranslateService,
        private appConfig: AppStartupService,
        private localize: LocalizeRouterService,
        private renderer: Renderer2,
        private customerAuthFacade: CustomerAuthFacade,
        private notification: NotificationFacade,
        private applicationFacade: ApplicationFacade,
        private activatedRoute: ActivatedRoute,
        private coreService: CoreService,
        private titleService: Title,
        public liveAnnouncer: LiveAnnouncer,
        private zone: NgZone,
        private domSanitizer: DomSanitizer,
        private matIconRegistry: MatIconRegistry,
        private onboardingService: OnboardingService,
        private authFacade: CustomerAuthFacade,
        private loginFacade: LoginFacade,
        private localeService: LocaleService,
        private route: ActivatedRoute,
        private mapApi: MapApiService,
        private printService: PrintService,
        private analyticsService: AnalyticsService,
        private pullToRefresh: PullToRefreshService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private session: SessionService,
        private deviceNotificationService: DeviceNotificationService,
        private network: NetworkService,
        private elementRef: ElementRef,
        private statusBarColor: StatusBarService,
        private accountTicketsService: AccountTicketsService,
        private ticketService: TicketsService,
        private accountTicketsFacade: AccountTicketsNgrxFacade,
        private keyboardService: KeyboardService,
        private mbtService: MtbService,
        private translations: TranslationsService,
        private cacheService: CacheService,
        private helperService: HelperService,
        private authService: AuthService,
        private widgetsService: WidgetsService,
        private messagesService: MessagesService,
        private authInterceptor: AuthInterceptor,
        private mobileHelperService: MobileHelperService,
        private modalFocusInterceptorService: ModalFocusInterceptorService,
        private dialog: MatDialog,
        private modalController: ModalController,
        private ngRedux: NgRedux<AppState>,
        private store: NgRedux<any>
    ) {
        super();
        this.titleService.setTitle(this.translate.instant('PAGE_TITLE'));
        if (this.env.SHOW_PWA) {
            this.setPWA();
        }
        this.firstLoad = true;
        if (Capacitor.isNativePlatform()) {
            this.initializeMobileApp();
        }
        this.initializeIconRegistry();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                requestAnimationFrame(() => {
                    if (Capacitor.getPlatform() === Platform.IOS) {
                        document.dispatchEvent(
                            new KeyboardEvent('keydown', {
                                key: 'Tab',
                                bubbles: true
                            })
                        );
                    }
                });
            });
    }

    private lastInteraction: 'pointer' | 'keyboard' = 'pointer';

    private routerEventSubscription: Subscription;
    cookiesAccepted$ = this.customerAuthFacade.isCookiesAccepted$;
    isContrast$ = this.applicationFacade.highContrast$;

    @ViewChild('accessibility') accessibilityBarEl: ElementRef;
    @ViewChild('cookie', { read: ElementRef }) cookieEl: ElementRef;
    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

    languages = [];
    selectedLang: Language;
    showFocusOutline = false;
    isLandingPage = false;
    displaySafeTop = true;
    skipToContentPath = '';
    shareLinkLocal = 'share-link';
    firstLoad = false;
    keyboardOpen = false;
    onboardingStatus: string;
    onboardingType = OnboardingType;
    showFooterForApp = true;
    exploreOrJPPage = false;
    corporatePortalPage = false;
    appFooterStatus?: AppFooterType = AppFooterType.navigation;
    appFooterType = AppFooterType;
    keyboardWillHideEvent: PluginListenerHandle;
    keyboardWillShowEvent: PluginListenerHandle;
    isPullToRefreshEnabled$ = this.pullToRefresh.isPullToRefreshEnabled$;
    isJpPrintAvailable = this.env.JOURNEY_PLANNER?.BUTTONS?.includes('SHOW_PRINT_BUTTON');
    isPdfPrintingNotInProgress$ = this.isJpPrintAvailable ? this.printService.isPdfPrintingNotInProgress$ : of(true);

    // TODO: this should not be here probably, will have to look in future tasks
    @select(['router']) route$;

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        const keyboardNavigationKeys = ['Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

        if (keyboardNavigationKeys.includes(event.key)) {
            this.lastInteraction = 'keyboard';
            this.showFocusOutline = true;
        }
    }

    @HostListener('document:focusin')
    handleFocusIn(): void {
        if (this.lastInteraction === 'keyboard') {
            this.showFocusOutline = true;
        }
    }

    @HostListener('document:pointerdown')
    handlePointerDown(): void {
        this.lastInteraction = 'pointer';
        this.showFocusOutline = false;
    }

    ngOnInit() {
        this.modalFocusInterceptorService.init();

        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            const selectedLanguage = this.translate.currentLang;
            this.terms = this.ngRedux.getState().core.region.terms;
            if (this.terms) {
                this.termsUrl = this.authService.buildTermsIframeUrl(this.terms?.contentBaseUrl, selectedLanguage);
            }
        }

        this.authService.currentTermsToAccept$.pipe(takeUntil(this.componentDestroyed$)).subscribe((newTerms) => {
            const selectedLanguage = this.translate.currentLang;
            this.terms = newTerms;

            if (this.terms) {
                this.termsUrl = this.authService.buildTermsIframeUrl(this.terms?.contentBaseUrl, selectedLanguage);
            }
        });

        if (Capacitor.isNativePlatform()) {
            const handleAppState = (isActive: boolean) => {
                if (isActive) {
                    this.authInterceptor.startPolling();
                } else {
                    this.authInterceptor.stopPolling();
                }
            };

            App.getState().then(({ isActive }) => handleAppState(isActive));
            App.addListener('appStateChange', ({ isActive }) => handleAppState(isActive)).then((listener) => {
                this.componentDestroyed$.subscribe(() => listener.remove());
            });
        } else if (this.env.ENABLE_TERMS_WMB_VERSION && this.authService.getLoginStatus()) {
            // This is for the T&C modal to also appear in browser
            this.authService
                .hasUserAgreedToLatest()
                .pipe(debounceTime(3000), take(1))
                .subscribe((hasAgreed) => {
                    if (!hasAgreed) {
                        this.authService
                            .fetchLatestTerms()
                            .pipe(take(1))
                            .subscribe(() => {
                                this.loginFacade.showTermsModal();
                            });
                    }
                });
        }

        this.routerEventSubscription = this.setTitleSubscription(this.translate.instant('PAGE_TITLE'));

        this.analyticsService.initializeAnalytics();

        if (!this.env.NO_THEMING) {
            this.setTheme();
            this.setFavicon();
        }
        if (this.env.IS_MOBILE_APP) {
            localStorage.setItem(`${this.env.APP_NAME}-cookies-accepted`, '1');
            this.authFacade.acceptCookies(true);
            this.statusBarColor.changeStatusBarStyle();
        }
        this.customerAuthFacade.acceptCookies(!!localStorage.getItem(`${this.env.APP_NAME}-cookies-accepted`));

        if (Capacitor.isNativePlatform() && this.env.ENABLE_BADGE_MESSAGES) {
            this.messagesService
                .getReceivedMessagesCount()
                .pipe(take(1))
                .subscribe(async ({ unread }) => {
                    await this.deviceNotificationService.setBadgeCount(unread);
                });
        }

        if (this.env.STATUS_BAR) {
            this.statusBarColor.handleToggleStatusBar$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((value: boolean) => {
                    this.displaySafeTop = value || false;
                });
        }

        this.loginFacade.termsModalVisible$.pipe(takeUntil(this.componentDestroyed$)).subscribe((visible) => {
            this.isFooterHidden = visible;
        });

        this.router.events.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.notification.prepareNext();
            }

            if (event instanceof NavigationEnd) {
                this.notification.removeAllExceptNext();
            }

            if (event instanceof RoutesRecognized && this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
                this.replaceCorrectLanguage(event);
            }

            if (event instanceof NavigationEnd && this.env.STATUS_BAR) {
                this.displaySafeTop = !event.url.includes('explore');
                this.statusBarColor.checkToSpecificPath(event.url);
            }
        });

        this.route$.pipe(takeUntil(this.componentDestroyed$)).subscribe((route) => {
            if (route.substr(-4) === 'home') {
                this.isLandingPage = true;
                this.renderer.addClass(this.document.body, 'display-only-landing');
            } else {
                this.isLandingPage = false;
                this.renderer.removeClass(this.document.body, 'display-only-landing');
            }

            let routerUrl = this.router.url;

            if (this.isJpPrintAvailable) {
                this.printService.emitPdfPrintingNotInProgress(
                    !routerUrl.includes('journey-planner/itinerary/details')
                );
            }

            if (routerUrl.indexOf('#') > -1) {
                routerUrl = routerUrl.split('#')[0];
            }
            this.skipToContentPath = `${routerUrl}#main-content`;

            this.cookiesAccepted$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cookiesAccepted) => {
                if (cookiesAccepted && !this.env.USE_TAILWIND) {
                    // Set focus on accessibility bar so "Skip to content" would be first tabbale element.
                    setTimeout(() => this.accessibilityBarEl?.nativeElement.focus());
                } else if (!cookiesAccepted) {
                    // Set focus on accept cookies on first load
                    setTimeout(() => this.cookieEl.nativeElement.focus());
                }
            });
        });

        this.isContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (localStorage.getItem('theme') === 'dark' || window.matchMedia('(prefers-contrast: more)').matches) {
                if (!isContrast) {
                    this.applicationFacade.switchHighContrastMode();
                }
                this.renderer.addClass(document.body, 'high-contrast');
                this.renderer.addClass(document.documentElement, 'dark');
            }
        });

        if (this.env.ONBOARDING?.ENABLE_ONBOARDING_FLOW) {
            this.onboardingService.initialized.pipe(takeUntil(this.componentDestroyed$)).subscribe((initialized) => {
                if (initialized && this.onboardingService.onboardingStatus !== OnboardingType.COMPLETED) {
                    this.setOnboardingFlow();
                }
            });
        }

        this.setThemeVariant();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.checkCurrentRoute();
                this.helperService.scrollToTop();
            });

        this.network
            .getStatus()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((status) => {
                if (this.env.IS_NEW_AFC) {
                    if (status) {
                        if (this.resetTicketsStateTimer) {
                            clearTimeout(this.resetTicketsStateTimer);
                            this.resetTicketsStateTimer = null;
                        }
                        localStorage.removeItem(RESET_TICKETS_TIME_CACHE);
                        localStorage.removeItem(TICKETS_CACHE_CLEARED);
                        this.reloadTicketsAfterOfflineClearIfNeeded();
                    } else {
                        this.regionResetTicketsTime$.pipe(take(1)).subscribe((resetTime: number) => {
                            if (resetTime) {
                                const localStorageTime = localStorage.getItem(RESET_TICKETS_TIME_CACHE);
                                if (localStorageTime) {
                                    if (+localStorageTime > Date.now() && !this.resetTicketsStateTimer) {
                                        this.resetTicketsStateTimer = setTimeout(
                                            () => this.removeCachedTickets(),
                                            +localStorageTime - Date.now()
                                        );
                                    } else {
                                        this.removeCachedTickets();
                                    }
                                } else {
                                    if (!this.resetTicketsStateTimer) {
                                        localStorage.setItem(
                                            RESET_TICKETS_TIME_CACHE,
                                            (Date.now() + resetTime * 60 * 1000).toString()
                                        );
                                        this.resetTicketsStateTimer = setTimeout(
                                            () => this.removeCachedTickets(),
                                            resetTime * 60 * 1000
                                        );
                                    }
                                }
                            }
                        });
                    }
                }

                if (
                    !status &&
                    this.env.OFFLINE_MODE?.DEFAULT_NAVIGATION &&
                    !this.router.url.includes('/account/tickets')
                ) {
                    this.router.navigate([this.translate.currentLang, ...this.env.OFFLINE_MODE.DEFAULT_NAVIGATION]);
                }
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.updateFooterVisibility();
            });

        this.authService.logoutBroadcast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((redirect) => {
            this.loginFacade.logout(redirect);
        });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.renderer.selectRootElement('gl-splash-screen').remove();
            this.splashScreenRemoved = true;

            const shadowMain = this.ionContentRef?.nativeElement?.shadowRoot?.querySelector('main');
            shadowMain?.setAttribute('role', 'none');
            this.applicationFacade.setReady(true);
        }, 2000);
        this.helperService.setIonContentRef(this.ionContent);

        if (this.env.ANALYTICS?.TYPE === VendorType.MATOMO) {
            this.downloadAllMissingTranslations();
        }
    }

    handleScrollStart() {
        this.widgetsService.notifyScrollStarted();
    }

    downloadAllMissingTranslations() {
        const localeData = this.localeService.getLocale();
        const defaultLang = this.translate.defaultLang;

        const otherLocaleCodes = localeData.locales?.filter(
            (code) => code && code !== defaultLang && !this.translate.store.translations[code]
        );

        if (Array.isArray(otherLocaleCodes) && otherLocaleCodes.length) {
            otherLocaleCodes.forEach((code) => {
                if (code) {
                    this.translations
                        .getTranslation(code)
                        .pipe(
                            takeUntil(this.componentDestroyed$),
                            catchError(() => {
                                console.error(`Translation for '${code}' failed to download.`);
                                return of({});
                            })
                        )
                        .subscribe((translation) => {
                            if (translation) {
                                this.translate.setTranslation(code, translation, true);
                            }
                        });
                }
            });
        }
    }

    checkCurrentRoute(): void {
        const url = this.router.url;
        this.exploreOrJPPage = url.includes('/explore') || url.includes('/journey-planner');
        this.corporatePortalPage = url.includes('/corporate-portal');
    }

    replaceCorrectLanguage(event) {
        const [_, language] = event.urlAfterRedirects.split('/');

        if (!this.localeService.menuChangeLanguage) {
            return;
        }

        if (language !== this.localeService.menuChangeLanguage && !event.urlAfterRedirects.includes('mobile-menu')) {
            const urlReplaced = event.urlAfterRedirects.replace(
                `/${language}/`,
                `/${this.localeService.menuChangeLanguage}/`
            );

            this.localeService.menuChangeLanguage = '';
            this.router.navigate([urlReplaced]);
        }
    }

    handleRefresh(event) {
        event.target.complete();
        this.pullToRefresh.callPullToRefresh();
    }

    private initializeMobileApp() {
        this.initNotifications();
        this.initAndroidFocusListener();
        this.initStatusBar();
        this.initDeepLinkListener();

        this.initKeyboardSubscriber();
        this.initSafeAreas();
    }

    private initNotifications() {
        if (this.env.APP_NOTIFICATIONS?.ENABLED) {
            this.deviceNotificationService.registerNotificationActionListener();
        }
    }

    private initAndroidFocusListener() {
        if (Capacitor.getPlatform() === 'android') {
            const url = this.router.url;
            const isExplore = url.includes('/explore');
            window.addEventListener('appFocusChanged', async (event: any) => {
                if (event.hasFocus && isExplore) {
                    this.openTripPlannerFacade.triggerLocationLocate();
                }
            });
        }
    }

    private initStatusBar() {
        if (this.env.STATUS_BAR && Capacitor.getPlatform() === 'android') {
            this.statusBarColor.overlayForAndroid();
            screen.orientation.addEventListener('change', () => {
                this.takeStatusBarHeight();
            });
            this.takeStatusBarHeight();
        }
    }

    private initDeepLinkListener() {
        localStorage.removeItem(this.shareLinkLocal);
        App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
            const isVerifyAccount = event.url.includes('verify-account');
            const isResetPassword = event.url.includes('reset-password');

            Browser.close();

            if (isResetPassword && this.session.getLoginStatus()) {
                return;
            }

            // Handle universal link for Password reset and Verification
            if (isVerifyAccount || isResetPassword) {
                const parsedUrl = new URL(event.url);
                const pathAndQuery = parsedUrl.pathname + parsedUrl.search;

                this.router.navigateByUrl(pathAndQuery);
                return;
            }

            // Handle universal link from Journey Planner
            if (event.url.includes('journey-planner/result') && this.env.JOURNEY_PLANNER?.SHOW_ROUTE_SHARE) {
                const parsedUrl = new URL(event.url);
                const pathAndQuery = parsedUrl.pathname + parsedUrl.search;

                if (this.onboardingService.onboardingStatus !== this.onboardingType.COMPLETED) {
                    localStorage.setItem(this.shareLinkLocal, pathAndQuery);
                    return;
                }

                this.zone.run(async () => {
                    if (pathAndQuery) {
                        await this.closeAllModalsAndDialogs();
                        // TODO: should redirect to onboarding flow
                        this.onboardingService.onboardingStatus = this.onboardingType.COMPLETED;
                        this.router.navigateByUrl(pathAndQuery);
                    }
                });
                // Handle payment portal return link missing language
            } else if (event.url.startsWith(this.env.PAYMENT_RETURN_URL)) {
                this.zone.run(() => {
                    timer(100).subscribe(() => {
                        const url = new URL(event.url);
                        const queryParams = Object.fromEntries(url.searchParams as any);
                        const path = url.host + url.pathname;
                        const segments = path.split('/').filter(Boolean);
                        this.router.navigate(segments, { queryParams });
                    });
                });
            } else {
                this.zone.run(() => {
                    const cleanedUrl = event.url.replace(/^https?:\/\/[^/]+\//, '');
                    if (cleanedUrl) {
                        this.router.navigateByUrl(cleanedUrl);
                    }
                });
            }
        });
    }

    private initSafeAreas() {
        this.mobileHelperService.safeAreaBottom$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.applySafeAreaVar('--ion-safe-area-bottom', value);
        });
    }

    private takeStatusBarHeight(): void {
        // Status-bar dimen stays valid when edge-to-edge stable insets report 0.
        // Bottom inset is owned by MobileHelperService.
        Promise.all([SafeArea.getStatusBarHeight(), SafeArea.getSafeAreaInsets()]).then(([bar, area]) => {
            const top = Math.max(bar.statusBarHeight ?? 0, area.insets?.top ?? 0) || 30;
            this.applySafeAreaVar('--ion-safe-area-top', top);
        });
    }

    private applySafeAreaVar(name: '--ion-safe-area-top' | '--ion-safe-area-bottom', pixels: number): void {
        const value = `${pixels}px`;
        this.elementRef.nativeElement.style.setProperty(name, value);
        document.documentElement.style.setProperty(name, value);
    }

    setTitleSubscription(appName: string): Subscription {
        return this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((event) => {
                const title =
                    event.title !== undefined ? appName + ' | ' + this.translate.instant(event['title']) : appName;
                this.titleService.setTitle(title);

                if (this.firstLoad) {
                    this.firstLoad = false;
                } else {
                    this.liveAnnouncer.announce(title);
                }
            });
    }

    ngOnDestroy() {
        this.routerEventSubscription.unsubscribe();

        if (Keyboard && this.env.HIDE_MENU_OPEN_KEYBOARD) {
            this.keyboardWillHideEvent?.remove();
            this.keyboardWillShowEvent?.remove();
        }

        super.ngOnDestroy();
    }

    async initKeyboardSubscriber() {
        if (Keyboard && this.env.HIDE_MENU_OPEN_KEYBOARD) {
            this.keyboardWillShowEvent = await Keyboard.addListener('keyboardWillShow', () => {
                this.keyboardOpen = true;
            });

            this.keyboardWillHideEvent = await Keyboard.addListener('keyboardWillHide', () => {
                this.keyboardOpen = false;
            });
        }
    }

    changeLanguage(lang: Language) {
        if (this.selectedLang?.code && lang?.code) {
            this.analyticsService.eventTrack(TrackedEventAction.CHANGED, {
                category: TrackedEventCategory.MENU,
                name: `${this.selectedLang.code} -> ${lang.code}`
            });
        }

        this.selectedLang = lang;
        this.localize.changeLanguage(lang.code, { replaceUrl: !!this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE });
        this.document.documentElement.lang = lang.code;

        // TODO: Will be improved with STW-121
        // this.applicationFacade.setSelectedLanguage( this.selectedLang );
        this.coreService.setSelectedLanguage(this.selectedLang);
        this.applicationFacade.updateTermsUrl();
    }

    toggleTheme() {
        this.applicationFacade.switchHighContrastMode();
        this.applicationFacade.highContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (isContrast) {
                this.renderer.addClass(document.body, 'high-contrast');
                this.renderer.addClass(document.documentElement, 'dark');
                localStorage.setItem('theme', 'dark');
            } else {
                this.renderer.removeClass(document.body, 'high-contrast');
                this.renderer.removeClass(document.documentElement, 'dark');
                localStorage.removeItem('theme');
            }

            const state = isContrast ? 'Enabled' : 'Disabled';
            this.analyticsService.eventTrack(TrackedEventAction.CHANGED, {
                category: TrackedEventCategory.MENU,
                name: `High contrast: ${state}`
            });
        });

        this.statusBarColor.checkToSpecificPath(window.location.href);
        this.statusBarColor.changeStatusBarStyle();
    }

    setTheme() {
        // SISE-6992 displaying different theme
        // not an actuall functionality gotland will use
        // setting it to default
        let style;
        // depending on build, output is either .js (ng serve) or .css (--prod)
        // so in settings.json set production=false in local env (ng serve...)
        if (this.env.production) {
            style = this.renderer.createElement('link');
            style.rel = 'stylesheet';
            style.type = 'text/css';
            style.href = `${this.env.THEME_NAME}.css`;
        } else {
            // console.log(`${this.env.THEME_NAME}.js`);
            // style = this.renderer.createElement('script');
            // style.type = 'text/javascript';
            // style.src = `${this.env.THEME_NAME}.js`;
            style = this.renderer.createElement('link');
            style.rel = 'stylesheet';
            style.type = 'text/css';
            style.href = `${this.env.THEME_NAME}.css`;
        }
        this.renderer.appendChild(this.document.getElementsByTagName('head')[0], style);
    }

    setFavicon() {
        const appFavicon: HTMLLinkElement = document.querySelector('#appIcon');

        if (this.env.USE_TAILWIND && !this.env.IS_MOBILE_APP) {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            // On initial page launch
            mediaQuery.matches
                ? (appFavicon.href = `${this.env.ASSETS_DIR}/favicon-light.ico`)
                : (appFavicon.href = `${this.env.ASSETS_DIR}/favicon-dark.ico`);

            // On theme change
            mediaQuery.addEventListener('change', () => {
                mediaQuery.matches
                    ? (appFavicon.href = `${this.env.ASSETS_DIR}/favicon-light.ico`)
                    : (appFavicon.href = `${this.env.ASSETS_DIR}/favicon-dark.ico`);
            });
        } else {
            appFavicon.href = `${this.env.ASSETS_DIR}/favicon.ico`;
        }
    }

    setPWA() {
        const manifest = this.renderer.createElement('link');
        manifest.rel = 'manifest';
        manifest.href = `manifest.${this.env.THEME_NAME}.webmanifest`;
        this.renderer.appendChild(this.document.getElementsByTagName('head')[0], manifest);

        const appleIcon = this.renderer.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.href = `./assets/${this.env.THEME_NAME}/icons/icon-192x192.png`;
        this.renderer.appendChild(this.document.getElementsByTagName('head')[0], appleIcon);
    }

    initializeIconRegistry(): void {
        this.matIconRegistry.addSvgIcon(
            'accessibility',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/accessibility.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'email',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/email.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'menu-feedback',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/menu-feedback.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'info',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/info.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'key',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/key.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'pencil',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/pencil.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'language',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/language.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'external-link',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/external-link.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'phone',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/phone.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'menu-icon-placeholder',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/placeholder.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'menu-icon-document',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/document.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'notification-settings',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/menu/notifications_settings.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'payment-settings',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/payment_settings.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'payment',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/payment.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'trash-bin',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/trash-bin.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'traffic',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/traffic.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'user-placeholder-icon',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/user.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'circle-question',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/circle-question.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'menu-icon-placeholder-disabled',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/placeholder-disabled.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'discount',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icon_form_discount.svg')
        );
        this.matIconRegistry.addSvgIcon(
            'ticket-back',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/ticket-details/back.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'single',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/purchase-flow-v2/products/single.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'booklet',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/purchase-flow-v2/products/booklet.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'period',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/purchase-flow-v2/products/period.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'ticket-period',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/cards/ticket-period.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'joint_ticket',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/purchase-flow-v2/products/joint_ticket.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'ticket-download',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/ticket-details/download.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-explore',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/explore.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-explore-active',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/explore-active.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-journey-planner',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/journey-planner.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-journey-planner-active',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/footer/journey-planner-active.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'footer-tickets',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/tickets.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-tickets-active',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/tickets-active.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'journey-planner-start',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/journey-planner/favorite.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'back-arrow',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/journey-planner/back-arrow.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'footer-cards',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/footer/cards.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'footer-disruption-messages',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                `${this.env.ASSETS_DIR}/img/footer/disruption-messages.svg`
            )
        );
        this.matIconRegistry.addSvgIcon(
            'feedback',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/feedback.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'purchase',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/purchase.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'coins',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/menu/coins.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'company-travel-card',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/card-add.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'facebook',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/facebook-logo.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'instagram',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/instagram-logo.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'twitter',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/twitter-logo.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'linkedin',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/linkedin-logo.svg`)
        );
        this.matIconRegistry.addSvgIcon(
            'youtube',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`${this.env.ASSETS_DIR}/img/youtube-logo.svg`)
        );
    }

    private setThemeVariant() {
        this.isContrast$
            .pipe(map((isContrast) => this.buildThemeVariantClasses(isContrast)))
            .subscribe(({ themeVariant, isDarkTheme }) => {
                this.applyThemeVariantToBody(themeVariant, isDarkTheme);
            });
    }

    private buildThemeVariantClasses(isContrast: boolean): { themeVariant: string; isDarkTheme: boolean } {
        const themeNameVariant = this.env.THEME_NAME;
        const themeVariantLight = `theme--${themeNameVariant}-light`;
        const themeVariantDark = `theme--${themeNameVariant}-dark`;
        const themeVariantLightDark = `theme--${themeNameVariant}-light-dark`;

        const themeClasses = [themeVariantLightDark];
        if (isContrast) {
            themeClasses.push(themeVariantDark);
        } else {
            themeClasses.push(themeVariantLight);
        }

        return { themeVariant: themeClasses.join(' '), isDarkTheme: isContrast };
    }

    private applyThemeVariantToBody(themeVariant: string, isDarkTheme: boolean): void {
        const body = document.body;

        // Remove existing theme classes
        Array.from(body.classList)
            .filter((className) => className.startsWith('theme--') || className === 'high-contrast')
            .forEach((className) => {
                body.classList.remove(className);
            });

        // Add new theme classes
        themeVariant.split(' ').forEach((className) => {
            body.classList.add(className);
        });

        // Add native-app class for native platforms/mobile apps
        // This is used for specific styling in native apps eg. content padding because of footer/tab bar
        if (Capacitor.isNativePlatform() || this.env.IS_MOBILE_APP) {
            body.classList.add('native-app');
        }

        // Toggle high-contrast class
        if (isDarkTheme) {
            body.classList.add('high-contrast');
        } else {
            body.classList.remove('high-contrast');
        }
    }

    updateFooterVisibility() {
        if (!this.env.HIDE_APP_FOOTER_FOR_ROUTES?.length) {
            this.isFooterHidden = false;
        }

        if (this.keyboardOpen) {
            this.isFooterHidden = true;
        }

        this.isFooterHidden = this.env.HIDE_APP_FOOTER_FOR_ROUTES?.some((route) => this.router.url.includes(route));
    }

    private setOnboardingFlow(): void {
        this.router.navigate([this.translate.currentLang, 'onboarding']);

        if (!this.onboardingService.onboardingStatus) {
            this.onboardingService.onboardingStatus = OnboardingType.STARTED;

            if (!this.env.ONBOARDING.SUB_PAGES.includes('LANGUAGE_SELECTION')) {
                this.onboardingService.onboardingStatus = OnboardingType.ONBOARD_FLOW;
            }
        }

        this.onboardingService.onboardingStatusValue
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((onboardingStatus: OnboardingType) => {
                if (onboardingStatus === OnboardingType.COMPLETED) {
                    this.appFooterStatus = AppFooterType.navigation;
                    this.router.navigate([this.translate.currentLang]);
                } else if (!this.isOnboardingInProgress(onboardingStatus) && !this.env.ONBOARDING.LOGIN.REQUIRE_LOGIN) {
                    this.appFooterStatus = AppFooterType.navigation;
                } else {
                    this.appFooterStatus = null;
                }
            });
    }

    private isOnboardingInProgress(onboardingStatus: OnboardingType): boolean {
        return [OnboardingType.STARTED, OnboardingType.ONBOARD_FLOW, OnboardingType.LOGIN_FLOW].includes(
            onboardingStatus
        );
    }

    private removeCachedTickets() {
        this.accountTicketsFacade.resetState();
        this.ticketService.resetState(true);
        this.cacheService.resetTicketsList();
        localStorage.removeItem(RESET_TICKETS_TIME_CACHE);
        localStorage.setItem(TICKETS_CACHE_CLEARED, Date.now().toString());
        this.resetTicketsStateTimer = null;
        this.shouldReloadTicketsOnOnline = true;
    }

    private reloadTicketsAfterOfflineClearIfNeeded(): void {
        if (!this.shouldReloadTicketsOnOnline) {
            return;
        }
        this.ticketService.reloadTickets();
        this.accountTicketsFacade.fetchAccountTickets(0, 0);
        this.shouldReloadTicketsOnOnline = false;
    }

    private async closeAllModalsAndDialogs(): Promise<void> {
        this.dialog.closeAll();
        let modal = await this.modalController.getTop();
        while (modal) {
            await modal.dismiss();
            modal = await this.modalController.getTop();
        }
    }

    buildTermsIframeUrl(termsUrl, language): string {
        const highContrast = this.store.getState().application.highContrast;
        return `${termsUrl}/${language}/${highContrast ? '#high-contrast' : ''}`;
    }

    onTermsAgreement(agreed: boolean) {
        if (agreed) {
            this.authService.agreeToLatestTerms(this.terms?.version).subscribe(() => {
                this.loginFacade.hideTermsModal();
            });
        } else {
            this.loginFacade.hideTermsModal();
            this.loginFacade.logout();
        }
    }
}
